<template>
  <div class="contact-select">
    <b-form-group
      label-class="mb-0 label-title"
      :label="title"
      label-for="contact-select"
    >
      <div class="contact-select__row">
        <v-select
          v-model="selectedContact"
          class="form-dropdown contact-select__dropdown"
          :options="contactOptions"
          :disabled="disabled || loading"
          :searchable="true"
          :clearable="false"
          label="label"
          placeholder="-- Please Select --"
          @input="handleSelected"
        />
        <b-button
          class="contact-select__add"
          size="sm"
          variant="outline-primary"
          :disabled="disabled"
          @click="openCreateModal"
        >
          Add New
        </b-button>
      </div>
      <small v-if="loading" class="text-muted d-block mt-1">
        Loading saved contacts...
      </small>
      <small v-else-if="contactOptions.length === 0" class="text-muted d-block mt-1">
        No saved reviewer contacts yet.
      </small>
      <small v-if="profileLink" class="text-muted d-block mt-1">
        You can manage reviewer contacts in
        <a :href="profileLink" target="_blank" rel="noopener noreferrer" style="text-decoration: underline;">Profile</a>.
      </small>
    </b-form-group>

    <b-modal
      v-model="showModal"
      title="Add Reviewer Contact"
      ok-title="Save"
      :ok-disabled="submitting"
      :busy="submitting"
      @ok="submitForm"
      @hidden="resetForm"
    >
      <b-form-group label="Full Name" label-for="contact-full-name">
        <b-form-input
          id="contact-full-name"
          v-model.trim="form.full_name"
          :state="inputState('full_name')"
          maxlength="255"
          required
        />
        <b-form-invalid-feedback v-if="firstError('full_name')">
          {{ firstError("full_name") }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Job Role / Title" label-for="contact-job-title">
        <b-form-input
          id="contact-job-title"
          v-model.trim="form.job_title"
          :state="inputState('job_title')"
          maxlength="255"
        />
        <b-form-invalid-feedback v-if="firstError('job_title')">
          {{ firstError("job_title") }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Email" label-for="contact-email">
        <b-form-input
          id="contact-email"
          v-model.trim="form.email"
          :state="inputState('email')"
          type="email"
          maxlength="255"
          required
        />
        <b-form-invalid-feedback v-if="firstError('email')">
          {{ firstError("email") }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Phone" label-for="contact-phone">
        <b-form-input
          id="contact-phone"
          v-model.trim="form.phone"
          :state="inputState('phone')"
          type="tel"
          maxlength="50"
          required
        />
        <b-form-invalid-feedback v-if="firstError('phone')">
          {{ firstError("phone") }}
        </b-form-invalid-feedback>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Providers from "@/providers";
import vSelect from "vue-select";

const DEFAULT_FORM = () => ({
  full_name: "",
  job_title: "",
  email: "",
  phone: "",
});

export default {
  name: "ContactSelect",
  components: {
    vSelect,
  },
  props: {
    title: {
      type: String,
      default: "Contact",
    },
    selectedEmail: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    currentContact: {
      type: Object,
      default: null,
    },
    profileLink: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      showModal: false,
      contacts: [],
      selectedContact: null,
      form: DEFAULT_FORM(),
      errors: {},
    };
  },
  computed: {
    contactOptions() {
      const contacts = this.contacts.map((contact) => this.normalizeContact(contact));

      if (!this.currentContact || !this.currentContact.email) {
        return contacts;
      }

      const savedMatch = contacts.find(
        (contact) => contact.email === this.currentContact.email
      );

      // job_title only from the saved contact record — never from form/profile.
      const current = this.normalizeContact({
        full_name: savedMatch?.full_name || this.currentContact.full_name,
        job_title: savedMatch?.job_title || "",
        email: this.currentContact.email,
        phone: savedMatch?.phone || this.currentContact.phone,
        current: true,
      });

      return [
        current,
        ...contacts.filter((contact) => contact.email !== current.email),
      ];
    },
  },
  watch: {
    selectedEmail() {
      this.selectContactByEmail(this.selectedEmail, false);
    },
    currentContact() {
      this.selectContactByEmail(this.selectedEmail, false);
    },
  },
  created() {
    this.loadContacts();
  },
  methods: {
    loadContacts() {
      this.loading = true;

      Providers.profile
        .getReviewerContacts()
        .then((items) => {
          this.contacts = Array.isArray(items) ? items : [];
          // Select for display only — do not emit, or we overwrite form role fields on every reload.
          this.selectContactByEmail(this.selectedEmail, false);
        })
        .catch((resp) => {
          this.contacts = [];
          this.$httpError("Failed to load reviewer contacts", resp);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    normalizeContact(contact) {
      const normalized = {
        id: contact?.id,
        full_name: contact?.full_name || "",
        job_title: contact?.job_title || "",
        email: contact?.email || "",
        phone: contact?.phone || "",
        current: !!contact?.current,
      };

      normalized.label = [
        normalized.current ? "(Current)" : null,
        normalized.full_name,
        normalized.job_title || null,
        normalized.email,
        normalized.phone || null,
      ].filter(Boolean).join(" - ");

      return normalized;
    },
    selectContactByEmail(email, emitSelection) {
      if (!email) {
        this.selectedContact = null;
        return;
      }

      const selected = this.contactOptions.find((contact) => contact.email === email) || null;
      this.selectedContact = selected;

      if (selected && emitSelection) {
        this.emitSelected(selected);
      }
    },
    handleSelected(contact) {
      if (!contact) {
        return;
      }

      this.emitSelected(contact);
    },
    emitSelected(contact) {
      this.$emit("selected", {
        id: contact.id,
        full_name: contact.full_name || "",
        job_title: contact.job_title || "",
        email: contact.email || "",
        phone: contact.phone || "",
        current: !!contact.current,
      });
    },
    openCreateModal() {
      this.resetForm();
      this.showModal = true;
    },
    resetForm() {
      this.form = DEFAULT_FORM();
      this.errors = {};
      this.submitting = false;
    },
    inputState(field) {
      if (!Object.prototype.hasOwnProperty.call(this.errors, field)) {
        return null;
      }
      return false;
    },
    firstError(field) {
      const fieldErrors = this.errors[field];
      return Array.isArray(fieldErrors) && fieldErrors.length > 0
        ? fieldErrors[0]
        : null;
    },
    validateClientSide() {
      const errors = {};

      if (!this.form.full_name) {
        errors.full_name = ["Full name is required."];
      }
      if (!this.form.email) {
        errors.email = ["Email is required."];
      }
      if (!this.form.phone) {
        errors.phone = ["Phone is required."];
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    submitForm(event) {
      event.preventDefault();

      if (!this.validateClientSide()) {
        return;
      }

      this.submitting = true;
      this.errors = {};

      const payload = {
        full_name: this.form.full_name,
        job_title: this.form.job_title || null,
        email: this.form.email,
        phone: this.form.phone,
      };

      Providers.profile
        .createReviewerContact(payload)
        .then((contact) => {
          const createdContact = this.normalizeContact(contact || payload);
          this.contacts = [
            ...this.contacts.filter((item) => item.email !== createdContact.email),
            createdContact,
          ];
          this.selectedContact = createdContact;
          this.emitSelected(createdContact);
          this.$success("Contact Added", "Reviewer contact added successfully.");
          this.showModal = false;
        })
        .catch((resp) => {
          this.errors = resp?.body?.errors || {};
          this.$httpError("Failed to add reviewer contact", resp);
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.contact-select__row {
  align-items: center;
  display: flex;
  gap: 8px;
}

.contact-select__dropdown {
  flex: 1 1 auto;
  min-width: 0;
}

.contact-select__add {
  flex: 0 0 auto;
  min-height: 38px;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .contact-select__row {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
